import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";
import SubscriptionCheckoutButton from "./SubscriptionCheckoutButton";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const MENU_ITEMS = [
  { key: "bookings", label: "Booking History", icon: "📦" },
  { key: "address", label: "Address", icon: "📍" },
  { key: "membership", label: "Membership", icon: "⭐" },
  { key: "subscription", label: "Subscription", icon: "🔁" },
  { key: "invite", label: "Invite Friends", icon: "👥" },
  { key: "settings", label: "Account Setting", icon: "⚙️" },
  { key: "logout", label: "Logout", icon: "↩️", isDanger: true },
];

const toIndianCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

export default function OwnerDashboardHome({
  user,
  onBrowse,
  onConsultVet,
  onMarketplace,
  onMyPets,
  onSaved,
  onMyBookings,
  onPayments,
  onSupport,
  onLogout,
}) {
  const [bookings, setBookings] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [aiPremiumStatus, setAiPremiumStatus] = useState({
    active: false,
    status: "inactive",
    planCode: "",
    until: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeSection, setActiveSection] = useState("bookings");
  const [inviteCopied, setInviteCopied] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  const isMobile = viewportWidth < 760;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!user?.uid || !user?.email) {
      setBookings([]);
      setUserProfile({});
      setAiPremiumStatus({
        active: false,
        status: "inactive",
        planCode: "",
        until: "",
      });
      return;
    }
    try {
      setLoading(true);
      setLoadError("");

      const ownerQ = query(collection(db, "bookings"), where("ownerEmail", "==", user.email));
      const legacyQ = query(collection(db, "bookings"), where("userEmail", "==", user.email));
      const profileRef = doc(db, "users", user.uid);

      const [ownerSnap, legacySnap, profileSnap] = await Promise.all([
        getDocs(ownerQ),
        getDocs(legacyQ),
        getDoc(profileRef),
      ]);

      const merged = [...ownerSnap.docs, ...legacySnap.docs];
      const byId = new Map(merged.map((item) => [item.id, { id: item.id, ...item.data() }]));
      setBookings([...byId.values()]);

      const profileData = profileSnap.exists() ? (profileSnap.data() || {}) : {};
      setUserProfile({
        name: String(
          profileData.name ||
            profileData.fullName ||
            profileData.displayName ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "Pet Parent",
        ),
        phone: String(profileData.phone || user.phoneNumber || ""),
        address: String(
          profileData.currentAddress ||
            profileData.address ||
            profileData.permanentAddress ||
            "",
        ),
      });

      const aiPremiumUntil = String(profileData.aiPremiumUntil || "");
      const aiPremiumUntilMs = Date.parse(aiPremiumUntil);
      const aiPremiumActive =
        String(profileData.aiPremiumStatus || "").toLowerCase() === "active" &&
        Number.isFinite(aiPremiumUntilMs) &&
        aiPremiumUntilMs > Date.now();

      setAiPremiumStatus({
        active: aiPremiumActive,
        status: aiPremiumActive
          ? "active"
          : String(profileData.aiPremiumStatus || "inactive").toLowerCase(),
        planCode: String(profileData.aiPremiumPlanCode || ""),
        until: aiPremiumUntil,
      });
      setLastUpdated(Date.now());
    } catch (error) {
      setLoadError(error.message || "Unable to load dashboard data.");
      setBookings([]);
      setUserProfile({});
      setAiPremiumStatus({
        active: false,
        status: "inactive",
        planCode: "",
        until: "",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.uid, user?.email, user.displayName, user.phoneNumber]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats = useMemo(() => {
    const paid = bookings.filter((booking) => booking.paymentStatus === "paid");
    const spend = paid.reduce((sum, booking) => sum + (Number(booking.amount) || 0), 0);
    const upcoming = bookings.filter((booking) =>
      ["pending_assignment", "pending", "accepted", "in_progress"].includes(booking.status),
    ).length;
    const completed = bookings.filter((booking) => booking.status === "completed").length;
    return {
      spend,
      upcoming,
      completed,
      total: bookings.length,
    };
  }, [bookings]);

  const recentBookings = useMemo(
    () =>
      [...bookings]
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
        .slice(0, 6),
    [bookings],
  );

  const membershipTier = useMemo(() => {
    if (stats.total >= 20) return "Platinum";
    if (stats.total >= 10) return "Gold";
    if (stats.total >= 3) return "Silver";
    return "Basic";
  }, [stats.total]);

  const referralCode = useMemo(
    () => `PETAAR-${String(user?.uid || "NEWUSER").slice(0, 6).toUpperCase()}`,
    [user?.uid],
  );
  const quickActions = useMemo(() => ([
    { label: "Book Service", icon: "🛎️", action: onBrowse },
    { label: "Consult Vet", icon: "🎥", action: onConsultVet },
    { label: "Marketplace", icon: "🛍️", action: onMarketplace },
    { label: "My Pets", icon: "🐶", action: onMyPets },
  ]), [onBrowse, onConsultVet, onMarketplace, onMyPets]);
  const recentActivity = useMemo(
    () =>
      recentBookings.map((booking) => ({
        id: booking.id,
        title: booking.serviceTitle || booking.serviceName || "Pet service",
        meta: `${booking.bookingDate || "No date"} • ${booking.status || "pending"}`,
        amount: toIndianCurrency(booking.amount),
      })),
    [recentBookings],
  );

  const handleMenuClick = (itemKey) => {
    if (itemKey === "logout") {
      if (typeof onLogout === "function") onLogout();
      return;
    }
    setActiveSection(itemKey);
  };

  const copyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1400);
    } catch (_) {
      alert("Copy failed. Please copy manually.");
    }
  };

  const renderBookingsPanel = () => (
    <>
      <div className="petaar-section-title">
        <div>
          <div className="petaar-kicker">Recent bookings</div>
          <h2 style={{ marginTop: 4 }}>Your Orders</h2>
        </div>
        <ToneBadge tone="default">{loading ? "Refreshing" : `${stats.total} bookings`}</ToneBadge>
      </div>
      <p style={{ marginTop: 0, color: "var(--muted)" }}>
        {loading ? "Loading orders..." : `${stats.total} total bookings`}
      </p>
      {recentBookings.length === 0 ? (
        <EmptyState
          icon="📦"
          title="No orders yet"
          description="Your latest bookings will appear here once you start using Petaar services."
          actionLabel={loading ? "Refreshing..." : "Refresh"}
          onAction={loadDashboard}
        />
      ) : (
        <div className="petaar-list-card">
          {recentBookings.map((booking) => (
            <div
              key={booking.id}
              className="petaar-list-row"
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div>
                  <strong style={{ display: "block", fontSize: "1.05rem" }}>
                    {booking.serviceTitle || booking.serviceName || "Service"}
                  </strong>
                  <div style={{ marginTop: 6, color: "#334155", fontSize: 14 }}>
                    Payment: <b>{booking.paymentStatus || "unknown"}</b> | Amount: <b>{toIndianCurrency(booking.amount)}</b>
                  </div>
                </div>
                <ToneBadge tone={booking.status === "completed" ? "success" : booking.status === "cancelled" ? "danger" : "warning"}>
                  {booking.status || "pending"}
                </ToneBadge>
              </div>
              <div style={{ marginTop: 8, color: "var(--muted)", fontSize: 13 }}>
                Date: {booking.bookingDate || "N/A"}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
        <button className="petaar-button" onClick={onMyBookings}>Open Booking History</button>
        <button className="petaar-button--secondary" onClick={onBrowse}>Book New Service</button>
        <button className="petaar-button--secondary" onClick={onMarketplace}>Open Marketplace</button>
      </div>
    </>
  );

  const renderAddressPanel = () => (
    <>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Address</h2>
      <div className="petaar-surface" style={{ padding: 20 }}>
        <p style={{ marginTop: 0, color: "#334155" }}>
          {userProfile.address || "No address added yet."}
        </p>
        <p style={{ marginBottom: 0, color: "#64748b", fontSize: 13 }}>
          Contact support to update your profile address.
        </p>
      </div>
      <div style={{ marginTop: 12 }}>
        <button className="petaar-button" onClick={onSupport}>Contact Support</button>
      </div>
    </>
  );

  const renderMembershipPanel = () => (
    <>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Membership</h2>
      <div className="petaar-surface" style={{ padding: 20 }}>
        <p style={{ marginTop: 0 }}>
          Current tier: <strong>{membershipTier}</strong>
        </p>
        <p style={{ marginTop: 0, marginBottom: 8, color: "#334155" }}>
          Completed bookings: <strong>{stats.completed}</strong> | Upcoming bookings: <strong>{stats.upcoming}</strong>
        </p>
        <p style={{ marginBottom: 0, color: "#64748b", fontSize: 13 }}>
          Keep booking on Petaar to unlock better offers and premium benefits.
        </p>
      </div>
    </>
  );

  const renderSubscriptionPanel = () => (
    <>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Subscription</h2>
      <div className="petaar-surface" style={{ padding: 20 }}>
        <p style={{ marginTop: 0 }}>
          AI Premium:{" "}
          <strong style={{ textTransform: "capitalize" }}>{aiPremiumStatus.status || "inactive"}</strong>
          {aiPremiumStatus.until ? ` (Valid till ${new Date(aiPremiumStatus.until).toLocaleDateString("en-IN")})` : ""}
        </p>
        <p style={{ marginTop: 0, color: "#334155" }}>
          Total spend on Petaar: <strong>{toIndianCurrency(stats.spend)}</strong>
        </p>
        <SubscriptionCheckoutButton
          planCode="ai_premium_monthly"
          buttonLabel={
            aiPremiumStatus.active
              ? "Extend AI Premium (₹199 / 30 days)"
              : "Activate AI Premium (₹199 / 30 days)"
          }
          onSuccess={loadDashboard}
        />
      </div>
    </>
  );

  const renderInvitePanel = () => (
    <>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Invite Friends</h2>
      <div className="petaar-surface" style={{ padding: 20 }}>
        <p style={{ marginTop: 0, marginBottom: 8 }}>Share your invite code:</p>
        <div
          style={{
            border: "1px dashed #93c5fd",
            borderRadius: 10,
            padding: "10px 12px",
            fontWeight: 800,
            color: "#1e3a8a",
            letterSpacing: 0.8,
            display: "inline-flex",
          }}
        >
          {referralCode}
        </div>
        <div style={{ marginTop: 10 }}>
          <button className="petaar-button" onClick={copyInviteCode}>{inviteCopied ? "Copied" : "Copy Code"}</button>
        </div>
      </div>
    </>
  );

  const renderSettingsPanel = () => {
    const fieldLabelStyle = {
      margin: "0 0 8px",
      color: "#0f172a",
      fontSize: isMobile ? 20 : 28,
      fontWeight: 800,
      letterSpacing: 0.2,
    };
    const fieldCardStyle = {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: "14px 12px",
      background: "#fff",
      minHeight: isMobile ? 46 : 52,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "#111827",
      fontSize: isMobile ? 17 : 26,
      boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
    };

    return (
      <>
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "14px 16px",
            background: "#fff",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            onClick={() => setActiveSection("bookings")}
            aria-label="Back to bookings"
            style={{
              border: "none",
              background: "transparent",
              color: "#111827",
              fontSize: isMobile ? 24 : 30,
              lineHeight: 1,
              padding: 0,
              cursor: "pointer",
            }}
          >
            ←
          </button>
          <h2 style={{ margin: 0, fontSize: isMobile ? 26 : 34, fontWeight: 500, color: "#111827" }}>Account Settings</h2>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <p style={fieldLabelStyle}>Name</p>
            <div style={fieldCardStyle}>
              <span>{userProfile.name || "Pet Parent"}</span>
              <button
                onClick={() => alert("Name update can be enabled in profile edit flow.")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#f97316",
                  fontSize: isMobile ? 20 : 24,
                  cursor: "pointer",
                }}
                aria-label="Edit name"
              >
                ✎
              </button>
            </div>
          </div>

          <div>
            <p style={fieldLabelStyle}>Email address</p>
            <div style={fieldCardStyle}>
              <span>{user?.email || "Not available"}</span>
            </div>
          </div>

          <div>
            <p style={fieldLabelStyle}>Mobile Number</p>
            <div style={fieldCardStyle}>
              <span>{userProfile.phone || "Not available"}</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  const panelBody = (() => {
    if (activeSection === "address") return renderAddressPanel();
    if (activeSection === "membership") return renderMembershipPanel();
    if (activeSection === "subscription") return renderSubscriptionPanel();
    if (activeSection === "invite") return renderInvitePanel();
    if (activeSection === "settings") return renderSettingsPanel();
    return renderBookingsPanel();
  })();

  return (
    <div className="petaar-page">
      <div className="petaar-card petaar-hero" style={{ padding: isMobile ? 20 : 28, marginBottom: 24 }}>
        <PageHeader
          eyebrow="Owner Dashboard"
          title={`Welcome back, ${userProfile.name || "Pet Parent"}! 🐾`}
          subtitle={`Logged in via ${userProfile.phone || user?.email || "your Petaar account"}${lastUpdated ? ` • Updated ${new Date(lastUpdated).toLocaleTimeString()}` : ""}`}
          actions={(
            <>
              <button className="petaar-button" onClick={onBrowse}>Book Service</button>
              <button className="petaar-button--secondary" onClick={onMyBookings}>View bookings</button>
            </>
          )}
        />
      </div>

      {loadError && (
        <div className="petaar-notice error" style={{ marginBottom: 16 }}>
          {loadError}
        </div>
      )}

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard icon="🐾" label="Total Pets" value={stats.total} tone="default" meta="Across bookings and care history" />
        <StatCard icon="📅" label="Active Bookings" value={stats.upcoming} tone="warning" meta="Upcoming or in progress" />
        <StatCard icon="✅" label="Completed Appointments" value={stats.completed} tone="success" meta="Successfully finished services" />
        <StatCard icon="⭐" label="Loyalty Value" value={Math.max(stats.completed * 25, 50)} tone="accent" meta="Points unlocked by repeat bookings" />
      </div>

      <div className="petaar-grid two" style={{ marginBottom: 24 }}>
        <div className="petaar-card">
          <div className="petaar-section-title">
            <div>
              <div className="petaar-kicker">Quick actions</div>
              <h2 style={{ marginTop: 4 }}>Move faster</h2>
            </div>
          </div>
          <div className="petaar-grid two">
            {quickActions.map((item) => (
              <button
                key={item.label}
                className="petaar-surface petaar-hover-card"
                onClick={item.action || item.altAction || onBrowse}
                style={{
                  padding: 18,
                  textAlign: "left",
                  display: "grid",
                  gap: 10,
                  cursor: "pointer",
                }}
              >
                <span className="petaar-stat-icon">{item.icon}</span>
                <strong style={{ fontSize: "1rem" }}>{item.label}</strong>
              </button>
            ))}
          </div>
        </div>
        <div className="petaar-card">
          <div className="petaar-section-title">
            <div>
              <div className="petaar-kicker">Recent activity</div>
              <h2 style={{ marginTop: 4 }}>Latest updates</h2>
            </div>
          </div>
          {recentActivity.length === 0 ? (
            <EmptyState
              icon="🗓️"
              title="No recent activity"
              description="Bookings, consultations, and other account events will show up here."
              actionLabel="Browse Services"
              onAction={onBrowse}
            />
          ) : (
            <div className="petaar-list-card">
              {recentActivity.map((item, index) => (
                <div key={item.id} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "center" }}>
                  <div className="petaar-stat-icon">{index + 1}</div>
                  <div>
                    <strong style={{ display: "block" }}>{item.title}</strong>
                    <small style={{ color: "var(--muted)" }}>{item.meta}</small>
                  </div>
                  <strong>{item.amount}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="petaar-sidebar-layout">
        <aside>
          <div className="petaar-sidebar-menu">
            {MENU_ITEMS.map((item) => {
              const active = activeSection === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => handleMenuClick(item.key)}
                  className={`petaar-sidebar-button${active ? " is-active" : ""}`}
                  style={{ color: item.isDanger ? "#991b1b" : "inherit" }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: isMobile ? 18 : 22 }}>{item.icon}</span>
                    <span style={{ fontSize: isMobile ? 15 : 17 }}>{item.label}</span>
                  </span>
                  <span style={{ color: active ? "#f97316" : "#64748b", fontSize: isMobile ? 18 : 20 }}>›</span>
                </button>
              );
            })}
          </div>
        </aside>

        <section style={{ minWidth: 0 }}>
          <div className="petaar-card">
            {panelBody}
          </div>
        </section>
      </div>
    </div>
  );
}
